import React, { useCallback, useState, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useHistory } from 'react-router-dom';

import { MdArrowBack, MdRefresh, MdRemoveRedEye } from 'react-icons/md';

import moment from 'moment';
import firebase from '../../../config/firebase';
import light from '../../../styles/themes/light';

import MessageConfirmation from '../../../utils/MessageConfirmation';
import MessageAlert from '../../../utils/MessageAlert';
import Button from '../../../components/Form/Button';
import Load from '../../../components/Load';

import Portfolio from './PdfContent';

import {
  Container,
  Header,
  Title,
  ButtonsContainer,
  PdfContent,
  FileNotFound,
  Content,
} from './styles';

interface IRouteParams {
  match: {
    params: {
      id: string;
    };
  };
}

interface IMachineData {
  description: string;
  description_english: string;
  departments: string[];
}

interface IDepartmentData {
  description: string;
  description_english: string;
}

interface IImageStoraged {
  url: string;
  name: string;
}

interface IBvspPartData {
  oemcode: string;
  bvspcode: string;
  description: string;
  description_insensitive: string;
  description_english: string;
  photos: IImageStoraged[];
  photocover: IImageStoraged;
  machines: string[];
}

const ExportPdfPartsByMachine: React.FC<IRouteParams> = ({ match }) => {
  const [pdfFileExistsInStorage, setPdfFileExistsInStorage] = useState<
    string
  >();
  const [loading, setLoading] = useState(true);
  const [machineName, setMachineName] = useState({ ptbr: '', usa: '' });
  const [pdfExistsPtbr, setPdfExistsPtbr] = useState(false);
  const [pdfExistsUsa, setPdfExistsUsa] = useState(false);
  const [languageSelector, setLanguageSelector] = useState('ptbr');

  const [rendering, setRendering] = useState(false);
  const history = useHistory();

  const { id: machineId } = match.params;

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleStorageFileUpdate = useCallback(
    async (fileUpload: Blob, language: string) => {
      const machine = await firebase
        .firestore()
        .collection('machines')
        .doc(machineId)
        .get()
        .then(async machineSnapshot => {
          const machineData = machineSnapshot.data() as IMachineData;
          setMachineName({
            ptbr: machineData.description,
            usa: machineData.description_english,
          });
          return {
            id: machineSnapshot.id,
            namePtBR: machineData.description,
            nameUsa: machineData.description_english,
          };
        });

      if (!machine) {
        setLoading(false);
        return MessageAlert('Máquina não encontrada!', 'error');
      }

      if (language !== 'ptbr') {
        await firebase
          .storage()
          .ref(`files/machines/usa/BVSP - ${machine.nameUsa}.pdf`)
          .put(fileUpload)
          .then(() => {
            window.location.reload();
          });
      } else {
        await firebase
          .storage()
          .ref(`files/machines/ptbr/BVSP - ${machine.namePtBR}.pdf`)
          .put(fileUpload)
          .then(() => {
            window.location.reload();
          });
      }
    },
    [machineId],
  );

  const handleDataPdf = useCallback(
    async (language: string) => {
      const { isConfirmed } = await MessageConfirmation(
        'Você tem certeza? Renderizar o PDF pode levar tempo.',
        'Sim. Renderizar!',
        'question',
      );

      if (isConfirmed) {
        setRendering(true);

        try {
          const machine = await firebase
            .firestore()
            .collection('machines')
            .doc(machineId)
            .get()
            .then(async machineSnapshot => {
              const machineData = machineSnapshot.data() as IMachineData;

              return {
                id: machineSnapshot.id,
                name:
                  language === 'ptbr'
                    ? machineData.description
                    : machineData.description_english,
                departments: machineData.departments,
              };
            });

          if (!machine) {
            setLoading(false);
            return MessageAlert('Máquina não encontrada!', 'error');
          }

          const department = await firebase
            .firestore()
            .collection('departments')
            .doc(machine.departments[0])
            .get()
            .then(departmentSnapshot => {
              const departmentData = departmentSnapshot.data() as IDepartmentData;

              return {
                id: departmentSnapshot.id,
                name:
                  language === 'ptbr'
                    ? departmentData.description
                    : departmentData.description_english,
              };
            });

          const parts = await firebase
            .firestore()
            .collection('parts')
            .where('machines', 'array-contains', machineId)
            .orderBy('description_insensitive')
            .get()
            .then(partSnapshot => {
              const partFormatted = partSnapshot.docs.map(doc => {
                const partData = doc.data() as IBvspPartData;

                let photo = partData.photos[0].url;

                if (partData.photocover) {
                  if (partData.photocover.url) {
                    photo = partData.photocover.url;
                  }
                }

                return {
                  id: doc.id,
                  name:
                    language === 'ptbr'
                      ? partData.description
                      : partData.description_english,
                  oemcode: partData.oemcode,
                  bvspcode: partData.bvspcode,
                  photo,
                };
              });

              return partFormatted;
            });

          if (language === 'ptbr') {
            moment.locale('pt');
            moment.updateLocale('pt', {
              months: [
                'Janeiro',
                'Fevereiro',
                'Março',
                'Abril',
                'Maio',
                'Junho',
                'Julho',
                'Agosto',
                'Setembro',
                'Outubro',
                'Novembro',
                'Dezembro',
              ],
            });
          }

          const pdfDate =
            language === 'ptbr'
              ? moment().format('MMMM [de] YYYY')
              : moment().format('MMMM YYYY');

          const dataPdf = {
            machine: {
              id: machine.id,
              name: machine.name,
              department: department.name,
              date: pdfDate,
              language,
            },
            parts,
          };

          const blob = await pdf(<Portfolio payload={dataPdf} />).toBlob();
          await handleStorageFileUpdate(blob, language);
        } catch (err) {
          return MessageAlert('Não foi possível gerar o PDF!', 'error');
        } finally {
          setRendering(false);
        }
      }
    },
    [machineId, handleStorageFileUpdate],
  );

  const getMachineName = useCallback(async () => {
    try {
      const machine = await firebase
        .firestore()
        .collection('machines')
        .doc(machineId)
        .get()
        .then(async machineSnapshot => {
          const machineData = machineSnapshot.data() as IMachineData;
          setMachineName({
            ptbr: machineData.description,
            usa: machineData.description_english,
          });
          return {
            id: machineSnapshot.id,
            namePtBr: machineData.description,
            nameUsa: machineData.description_english,
          };
        });

      if (!machine) {
        setLoading(false);
        return MessageAlert('Máquina não encontrada!', 'error');
      }

      let url = `files/machines/ptbr/BVSP - ${machine.namePtBr}.pdf`;
      try {
        await firebase
          .storage()
          .ref(url)
          .getDownloadURL()
          .then(linkFile => {
            setPdfFileExistsInStorage(linkFile);
            setPdfExistsPtbr(true);
          })
          .finally(() => setLoading(false));
      } catch (err) {}

      url = `files/machines/usa/BVSP - ${machine.nameUsa}.pdf`;
      await firebase
        .storage()
        .ref(url)
        .getDownloadURL()
        .then(() => {
          setPdfExistsUsa(true);
        })
        .finally(() => setLoading(false));
    } catch (err) {}
  }, [machineId]);

  useEffect(() => {
    getMachineName();
  }, [getMachineName]);

  useEffect(() => {
    setLoading(true);
    if (machineName.ptbr !== '') {
      let url = `files/machines/usa/BVSP - ${machineName.usa}.pdf`;

      if (languageSelector === 'ptbr') {
        url = `files/machines/ptbr/BVSP - ${machineName.ptbr}.pdf`;
      }
      setPdfFileExistsInStorage('');
      firebase
        .storage()
        .ref(url)
        .getDownloadURL()
        .then(linkFile => {
          setPdfFileExistsInStorage(linkFile);
        })
        .finally(() => setLoading(false));
    }
  }, [languageSelector, machineName]);

  return (
    <Container>
      <Header>
        <Title>Portfólio de Peças por Máquina</Title>
        <ButtonsContainer>
          <Button
            type="button"
            color={light.colors.primary}
            onClick={handleBack}
          >
            <MdArrowBack />
            Voltar
          </Button>
          <Button
            type="button"
            color={light.colors.success}
            onClick={() => {
              handleDataPdf('ptbr');
              setLanguageSelector('ptbr');
            }}
          >
            <MdRefresh />
            {pdfExistsPtbr ? 'Atualizar ' : 'Renderizar '}
            Português
          </Button>
          {pdfExistsPtbr && (
            <Button
              type="button"
              color={
                languageSelector !== 'ptbr'
                  ? light.colors.success
                  : light.colors.tertiary
              }
              onClick={() => setLanguageSelector('ptbr')}
            >
              <MdRemoveRedEye />
              Ver Português
            </Button>
          )}
          <Button
            type="button"
            color={light.colors.success}
            onClick={() => {
              handleDataPdf('enus');
              setLanguageSelector('usa');
            }}
          >
            <MdRefresh />
            {pdfExistsUsa ? 'Atualizar ' : 'Renderizar '}
            Inglês
          </Button>
          {pdfExistsUsa && (
            <Button
              type="button"
              color={
                languageSelector === 'ptbr'
                  ? light.colors.success
                  : light.colors.tertiary
              }
              onClick={() => setLanguageSelector('usa')}
            >
              <MdRemoveRedEye />
              Ver Inglês
            </Button>
          )}
        </ButtonsContainer>
      </Header>
      {loading ? (
        <Load />
      ) : (
        <Content>
          {!rendering && pdfFileExistsInStorage ? (
            <PdfContent src={pdfFileExistsInStorage} type="application/pdf" />
          ) : (
            <FileNotFound>
              {rendering
                ? 'Estamos gerando o PDF. Aguarde, isso pode levar minutos.'
                : 'No momento não há um PDF disponível. Renderize o arquivo.'}
            </FileNotFound>
          )}
        </Content>
      )}
    </Container>
  );
};

export default ExportPdfPartsByMachine;
